/**
 * Garde-fous de lisibilité de la marque blanche (#31).
 *
 * Un organisme choisit sa couleur primaire seul, devant un sélecteur, sans
 * savoir qu'un jaune vif rendra ses boutons illisibles pour tout le monde — et
 * c'est la réputation du produit qui en porte la trace, pas la sienne.
 *
 * Fonctions pures, épinglées dans `contrast.spec.ts` : l'arithmétique des
 * couleurs se trompe en silence, et une erreur de luminance ne se voit qu'à
 * l'œil, trop tard.
 */

/** Surfaces sur lesquelles la couleur de marque est réellement posée. */
export const SURFACES = {
  light: '#FBF5FF',
  dark: '#1E1724',
} as const

/** Seuil WCAG AA pour du texte normal. En dessous, le texte n'est pas lisible. */
export const AA_TEXT = 4.5

/**
 * Seuil WCAG AA pour un composant d'interface (bouton plein, bordure active).
 * Plus bas que le texte : c'est la FORME qui doit se détacher, pas des lettres.
 */
export const AA_UI = 3

/** `#abc` ou `#aabbcc` en composantes 0-255, ou `null` si la valeur est illisible. */
export function parseHex(value: string): [number, number, number] | null {
  const hex = value.trim().replace(/^#/, '')
  const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex
  if (!/^[0-9a-f]{6}$/i.test(full)) return null
  return [
    Number.parseInt(full.slice(0, 2), 16),
    Number.parseInt(full.slice(2, 4), 16),
    Number.parseInt(full.slice(4, 6), 16),
  ]
}

/**
 * Luminance relative WCAG.
 *
 * La correction gamma (le `** 2.4`) n'est pas décorative : sans elle, un jaune
 * et un bleu de même valeur numérique paraissent aussi lumineux, alors que
 * l'œil voit le jaune bien plus clair. C'est elle qui fait que le calcul
 * correspond à ce qu'on voit.
 */
export function luminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb.map((channel) => {
    const c = channel / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }) as [number, number, number]
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Rapport de contraste entre deux couleurs, de 1 (identiques) à 21 (noir/blanc). */
export function contrastRatio(a: string, b: string): number | null {
  const rgbA = parseHex(a)
  const rgbB = parseHex(b)
  if (!rgbA || !rgbB) return null
  const lumA = luminance(rgbA)
  const lumB = luminance(rgbB)
  const [light, dark] = lumA > lumB ? [lumA, lumB] : [lumB, lumA]
  return (light + 0.05) / (dark + 0.05)
}

export type PairVerdict = {
  /** Contraste entre l'encre choisie et le fond choisi : la lisibilité du texte. */
  textRatio: number
  textReadable: boolean
  /** Contraste du fond du couple sur la surface de la page : le bouton se détache-t-il ? */
  surfaceRatio: number
  surfaceVisible: boolean
  /** Encre qui contrasterait le mieux. PROPOSÉE, jamais imposée. */
  suggestedForeground: '#FFFFFF' | '#111111'
}

/**
 * Verdict sur un COUPLE fond/encre, posé sur une surface donnée.
 *
 * Deux questions distinctes, que juger une couleur seule confondait :
 * — l'encre se lit-elle sur le fond (le texte du bouton) ?
 * — le fond se détache-t-il de la page (le bouton se voit-il) ?
 *
 * Une marque bleu nuit en encre noire répond oui à la seconde et non à la
 * première : le bouton se voit parfaitement, son libellé est illisible. C'est
 * exactement le cas qu'un ratio unique laissait passer.
 */
export function judgePair(
  background: string,
  foreground: string,
  surface: string,
): PairVerdict | null {
  const suggestedForeground = readableForeground(background)
  if (!suggestedForeground) return null

  // Encre vide = celle que le front appliquera par défaut. Le simulateur doit
  // montrer ce qui SERA affiché, pas un trou.
  const ink = parseHex(foreground) ? foreground : suggestedForeground
  const text = contrastRatio(background, ink)
  const onSurface = contrastRatio(background, surface)
  if (text == null || onSurface == null) return null

  return {
    textRatio: Math.round(text * 100) / 100,
    textReadable: text >= AA_TEXT,
    surfaceRatio: Math.round(onSurface * 100) / 100,
    surfaceVisible: onSurface >= AA_UI,
    suggestedForeground,
  }
}

/**
 * Couleur de texte à poser SUR une couleur de fond : celle des deux encres qui
 * contraste le mieux.
 *
 * ⚠️ Indispensable dès qu'une couleur de marque remplace un jeton de thème.
 * Les thèmes posent un couple cohérent — fond noir / texte blanc en clair,
 * l'inverse en sombre — et le branding ne remplaçait que le FOND. Une marque
 * bleu nuit héritait donc du texte noir du thème sombre, et le bouton devenait
 * illisible ; une marque claire héritait du texte blanc du thème clair, et
 * cassait pareil dans l'autre sens.
 */
export function readableForeground(color: string): '#FFFFFF' | '#111111' | null {
  const onWhite = contrastRatio(color, '#FFFFFF')
  const onInk = contrastRatio(color, '#111111')
  if (onWhite == null || onInk == null) return null
  return onWhite >= onInk ? '#FFFFFF' : '#111111'
}
